import { useMemo, useState } from "react";
import tw, { theme } from "twin.macro";
import Button from "@atoms/Button";
import Heading from "@atoms/Heading";
import Modal from "@atoms/Modal";
import Paragraph from "@atoms/Paragraph";
import Label from "@atoms/Label";
import Card from "@atoms/Card";
import IconHeart from "@iconsV2/Controllers/heart-gradient.svg?react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { formatCurrency } from "@/utils/money";
import { openChat } from "@/utils/openChat";
import { StyledCodeCopy, StyledGradient } from "./styles";

export enum EStoreReview {
  EXCELLENT = "excelente",
  GOOD = "bueno",
  REGULAR = "regular",
  BAD = "malo",
  DISGUSTING = "pesimo",
}

interface IModalStoreReview {
  isOpen: boolean;
  handleClose: () => void;
  review: EStoreReview;
}

const discountAmount = 20000;
const discountCode = "buenofit";

const ModalStoreReview = ({ isOpen, handleClose, review }: IModalStoreReview) => {
  const [copied, setCopied] = useState(false);
  const isMedium = useMediaQuery("(min-width: {medium})");

  const isGoodReview = useMemo(() => {
    return [EStoreReview.GOOD, EStoreReview.EXCELLENT].includes(review);
  }, [review]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(discountCode);
    setCopied(true);
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      closeOutSise
      preventScroll
      classes={{
        card: tw`p-0 border-0 medium:max-w-[30rem] w-full overflow-auto`,
      }}
      fullScreen={!isMedium}
    >
      <StyledGradient $isGoodReview={isGoodReview}>
        <div tw="p-m rounded-full bg-neutral-100 mb-s">
          <IconHeart width={28} height={28} />
        </div>

        <Heading type="h5" color={theme`colors.neutral.100`} fontWeight={700}>
          ¡Gracias por tu opinión!
        </Heading>
        <Paragraph sizeVariant="sm" color={theme`colors.neutral.100`} fontWeight={500}>
          Tu experiencia nos ayuda a mejorar cada día
        </Paragraph>
      </StyledGradient>

      <form onSubmit={() => {}} tw="flex flex-col items-center text-center gap-m p-ml">
        <Heading type="h6" fontWeight={700}>
          {isGoodReview ? "¡Gracias por tu visita!" : "Realmente lamentamos tu experiencia"}
        </Heading>

        <div tw="flex flex-col items-center text-center">
          {isGoodReview ? (
            <Paragraph sizeVariant="sm" fontWeight={500}>
              Nos complace saber que tuviste una buena experiencia. Trabajamos constantemente para mejorar
              nuestro servicio.
              <span tw="text-secondary-50"> ¡Tu opinión es muy valiosa para nosotros!</span>
            </Paragraph>
          ) : (
            <Paragraph sizeVariant="sm" fontWeight={500}>
              Nos disculpamos sinceramente por no cumplir con tus expectativas. Queremos hacer las cosas bien
              y recuperar tu confianza.
              <span tw="text-secondary-50"> ¡Tu opinión es muy valiosa para nosotros!</span>
            </Paragraph>
          )}
        </div>

        <Card
          withShadow={false}
          active
          classes={{
            root: tw`w-full flex flex-col gap-1 text-left bg-primaryV2-90`,
          }}
        >
          <Label sizeVariant="md">¡Tienes {formatCurrency(discountAmount)} de descuento especial!</Label>
          <Paragraph sizeVariant="sm" fontWeight={500}>
            {isGoodReview
              ? "Gracias por elegirnos, aquí tienes una recompensa"
              : "Permítenos compensarte y demostrar que podemos hacerlo mejor"}
          </Paragraph>

          <StyledCodeCopy>
            <Paragraph
              sizeVariant="md"
              tw="uppercase tracking-widest"
              color={theme`colors.neutral.100`}
              fontWeight={600}
              onClick={handleCopy}
            >
              {discountCode}
            </Paragraph>
          </StyledCodeCopy>

          <Paragraph
            sizeVariant="sm"
            textAlign="center"
            fontWeight={500}
            onClick={handleCopy}
            variant="text"
            color="neutral"
            tw="italic mt-xs cursor-pointer"
          >
            {copied ? "¡Código copiado correctamente!" : "Haz clic para copiar el código"}
          </Paragraph>
        </Card>

        <Button sizeVariant="lg" color="secondary" fullWidth onClick={handleClose}>
          {isGoodReview ? "Continuar comprando" : "Cerrar"}
        </Button>

        <hr tw="bg-neutral-80 w-full h-px" />

        <Paragraph sizeVariant="sm">
          ¿Necesitas ayuda?{" "}
          <span tw="text-secondary-50 cursor-pointer" onClick={openChat}>
            Chatea con nosotros
          </span>
        </Paragraph>
      </form>
    </Modal>
  );
};

export default ModalStoreReview;
